import { useApiClient } from '@commander/api-client';
import { CheckCircle2, toast } from '@commander/ui';
import { useUserDetails } from '@commander/user-details';
import { RiDeleteBinLine, RiFileTextLine } from '@remixicon/react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import twentyLogo from '../../../../assets/avif_icons/twenty_logo.avif';
import zohoLogo from '../../../../assets/avif_icons/zoho-logo.png';
import {
  CalendlyLogo,
  HubSpotLogo,
  LinkedInLogo,
  PipedriveLogo,
  SalesforceLogo,
} from '../../../../assets/icons/AuthIcon';
import { getDynamicsAuthUrl } from '../../../integrations';
import { createOnboardingService } from '../../services/onboarding.service';
import type { StepProps } from '../../types/onboarding.types';
import { INTEGRATION_ACTIONS, INTEGRATION_OPTIONS } from '../../utils/constants';
import DynamicsLogo from '../shared/DynamicsLogo';
import DynamicsUrlModal from './IntegrationsStep/modals/DynamicsUrlModal';
import TwentyConnectModal from './IntegrationsStep/modals/TwentyConnectModal';

const IntegrationsStep: React.FC<StepProps> = ({
  data,
  onChange,
  validation,
  onNext,
  isLoading,
}) => {
  const [_localError, setLocalError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [fileProgress, setFileProgress] = useState<Map<number, number>>(new Map());
  const [fileLoading, setFileLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [showDynamicsModal, setShowDynamicsModal] = useState(false);
  const [showTwentyModal, setShowTwentyModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // API hooks
  const apiClient = useApiClient();
  const { userDetails } = useUserDetails();
  const onboardingService = createOnboardingService(apiClient);

  // Get company ID from user details or localStorage
  const getCompanyId = () => {
    if (userDetails?.company?.id) {
      return userDetails.company.id;
    }

    // Fallback to localStorage
    const userDetailsString: any = localStorage.getItem('user_details');
    const user = userDetailsString
      ? JSON.parse(userDetailsString)
      : { company_employee: { company: { id: '' } } };
    return user.company_employee?.company?.id || '';
  };

  useEffect(() => {
    setLocalError(null);
  }, []);

  const getIntegrationIcon = (iconId: string) => {
    switch (iconId) {
      case 'hubspot':
        return <HubSpotLogo />;
      case 'salesforce':
        return <SalesforceLogo />;
      case 'linkedin':
        return <LinkedInLogo />;
      case 'calendly':
        return <CalendlyLogo />;
      case 'microsoftDynamics':
        return <DynamicsLogo />;
      case 'twentyCRM':
        return <img src={twentyLogo} alt="Twenty CRM" className="max-h-12" />;
      case 'pipedrive':
        return <PipedriveLogo />;
      case 'zohoCRM':
        return <img src={zohoLogo} alt="Zoho CRM" className="max-h-12" />;
      default:
        return;
    }
  };

  const handleIntegrationToggle = (integrationId: string) => {
    // Check for modal-based integrations
    if (integrationId === 'microsoftDynamics') {
      setShowDynamicsModal(true);
      setSelectedCard(INTEGRATION_OPTIONS.findIndex((opt) => opt.id === integrationId));
      return;
    }

    if (integrationId === 'twentyCRM') {
      setShowTwentyModal(true);
      setSelectedCard(INTEGRATION_OPTIONS.findIndex((opt) => opt.id === integrationId));
      return;
    }

    // Handle direct URL-based integrations
    const action = INTEGRATION_ACTIONS[integrationId as keyof typeof INTEGRATION_ACTIONS];
    const userId = userDetails?.id || '';
    if (action && userId) {
      window.location.href = action(userId);
    }
  };

  // Modal handlers
  const handleDynamicsConnect = (cleanUrl: string) => {
    const userId = userDetails?.id || '';
    window.location.href = getDynamicsAuthUrl(userId, cleanUrl);
  };

  const resetDynamicsModal = () => {
    setSelectedCard(null);
    setShowDynamicsModal(false);
  };

  const resetTwentyModal = () => {
    setSelectedCard(null);
    setShowTwentyModal(false);
  };

  const handleTwentyConnect = async (values: { apiKey: string; crmUrl: string }) => {
    const { apiKey, crmUrl } = values;
    const companyId = userDetails?.company_employee?.company?.id || '';
    const response = await onboardingService.createTwentyCRMApi({ companyId, apiKey, crmUrl });
    if (response?.success || response?.status === 200) {
      resetTwentyModal();
      toast.success('Twenty CRM connected successfully');
      // Update the integrations array to check the checkbox
      const updatedIntegrations = [...data.integrations, 'twentyCRM'];
      onChange({ ...data, integrations: updatedIntegrations });
    } else {
      toast.error('Error connecting Twenty CRM');
    }
  };

  const isIntegrationConnected = (integrationId: string) => {
    return data.integrations.includes(integrationId);
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target || !event.target.files) return;

    const uploadedFiles: File[] = Array.from(event.target.files);
    const allowedExtensions = ['.csv'];
    const maxSizeBytes = 30 * 1024 * 1024; // 30MB
    const validFiles: File[] = [];

    uploadedFiles.forEach((file) => {
      if (allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))) {
        if (file.size <= maxSizeBytes) {
          validFiles.push(file);
          setUploadError('');
        } else {
          setUploadError(`${file.name} exceeds the maximum allowed size of 30MB.`);
        }
      } else {
        setUploadError('Only CSV files are supported!');
      }
    });

    setFiles((prevFiles) => [...prevFiles, ...validFiles]);

    // Upload files with progress
    validFiles.forEach(async (file, index) => {
      const fileIndex = files.length + index;
      const companyId = getCompanyId();

      if (!companyId) {
        setUploadError('Company ID not found. Please refresh the page and try again.');
        return;
      }

      setFileProgress((prevProgress) => new Map(prevProgress.set(fileIndex, 0)));
      setFileLoading(true);

      try {
        // Simulate progress during upload
        const progressInterval = setInterval(() => {
          setFileProgress((prevProgress) => {
            const currentProgress = prevProgress.get(fileIndex) || 0;
            if (currentProgress < 90) {
              return new Map(prevProgress.set(fileIndex, currentProgress + 10));
            }
            return prevProgress;
          });
        }, 100);

        // Upload file to API
        const response = await onboardingService.uploadProspects(companyId, file);

        // Clear progress interval
        clearInterval(progressInterval);

        // Set progress to 100% on success
        setFileProgress((prevProgress) => new Map(prevProgress.set(fileIndex, 100)));

        if (response.success) {
          setFileLoading(true);
          setUploadError('');
        } else {
          // Handle specific error messages
          if (response.message === 'Missing required fields: Company Name or Company Domain') {
            setUploadError('Missing required fields: Company Name or Company Domain');
          } else if (response.message === 'Network Error') {
            setUploadError(
              'Network connection failed. Please check your internet connection and try again.'
            );
          } else {
            setUploadError(response.message || 'File upload failed. Please try again.');
          }

          // Reset progress on error
          setFileProgress((prevProgress) => {
            const newProgress = new Map(prevProgress);
            newProgress.delete(fileIndex);
            return newProgress;
          });
          setFileLoading(false);
        }
      } catch (error: any) {
        // Clear any running progress interval
        setFileProgress((prevProgress) => {
          const newProgress = new Map(prevProgress);
          newProgress.delete(fileIndex);
          return newProgress;
        });

        setFileLoading(false);

        // Handle different error scenarios
        if (error.response?.data?.message) {
          setUploadError(error.response.data.message);
        } else if (error.message === 'Network Error') {
          setUploadError(
            'Network connection failed. Please check your internet connection and try again.'
          );
        } else {
          setUploadError('File upload failed. Please try again.');
        }

        console.error('File upload error:', error);
      }
    });
  };

  const handleFileDelete = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    const fileInputElement = fileInputRef.current;
    if (fileInputElement) {
      fileInputElement.value = '';
    }
  };

  const handleSubmit = async () => {
    const isValid = await validation(data);
    if (isValid) {
      setLocalError(null);
      onNext();
    } else {
      setLocalError('An error occurred. Please try again.');
    }
  };

  const handleSkip = () => {
    onChange({ ...data, integrations: [] });
    onNext();
  };

  return (
    <>
      <div className="flex-row justify-between bg-[#ffffff]">
        {/* Center Content */}
        <div className="w-full flex flex-wrap justify-center">
          <div className="w-full flex justify-center">
            <div className="text-center w-full max-w-[900px]">
              {/* Integration Cards Grid */}
              <div className="w-full flex justify-center">
                <div className="flex flex-wrap gap-6 justify-start w-full">
                  {INTEGRATION_OPTIONS.map((integration, index) => {
                    const isConnected = isIntegrationConnected(integration.id);

                    return (
                      <button
                        type="button"
                        key={integration.id}
                        className="w-[159px] md:w-[180px] lg:w-[200px] text-left"
                        onClick={() => handleIntegrationToggle(integration.id)}
                      >
                        <div
                          className={`
                            cursor-pointer h-[100px] p-4 bg-white w-full border rounded-lg relative flex items-center justify-center transition-all duration-200
                            ${
                              isConnected
                                ? 'border-2 border-primary bg-green-50'
                                : selectedCard === index
                                  ? 'border-2 border-primary'
                                  : 'border-[#D5DDE5] hover:border-gray-300 hover:shadow-md'
                            }
                          `}
                        >
                          <div className="flex flex-col items-center gap-3">
                            {getIntegrationIcon(integration.icon)}
                          </div>

                          {/* Checkbox */}
                          <div className="absolute right-[8px] top-[8px]">
                            <input
                              type="checkbox"
                              id={integration.id}
                              checked={isConnected}
                              readOnly
                              onClick={(e) => e.stopPropagation()}
                              className="pointer-events-none w-4 h-4 rounded border checked:bg-primary border-gray-300 bg-gray-100 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
                            />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* File Upload Section */}
              <div className="w-full flex pt-9 justify-center">
                <div className="w-full">
                  <div className="mb-6 rounded-lg flex items-start bg-white flex-wrap w-full">
                    <div
                      className={`w-full flex flex-col items-start justify-between ${files.length ? '!w-2/4' : ''}`}
                    >
                      <div className={`w-full mb-4 md:w-3/4 ${files.length ? '!w-full' : ''}`}>
                        <h2 className="text-gray-900 mb-[8px] text-start">
                          Got Lists? Drop it like it's hot!
                        </h2>
                        <p className="text-gray-600 text-start">File type: .csv Max size: 30MB</p>
                      </div>

                      <input
                        type="file"
                        id="file"
                        hidden
                        multiple
                        ref={fileInputRef}
                        accept=".csv"
                        onChange={handleFileChange}
                      />

                      <div
                        className={`w-full max-w-[132px] min-h-[45px] md:w-1/4 ${files.length ? '!w-40 mt-4' : ''}`}
                      >
                        <button
                          type="button"
                          className="bg-primary text-nowrap !text-white enabled:hover:bg-primary/80 rounded-[4px] border border-primary-600 md:w-full px-4 py-2 text-sm font-medium transition-colors duration-200"
                          onClick={handleUploadClick}
                        >
                          <div className="flex items-center justify-center gap-2">Upload away!</div>
                        </button>
                        <div className="w-full mt-1">
                          <span className="whitespace-nowrap text-xs font-normal text-red-600 text-start">
                            {uploadError}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* File List */}
                    <div className="w-full md:w-2/4 max-h-32 overflow-y-auto">
                      {files.length > 0 &&
                        files.map((file: File, index: number) => (
                          <div className="w-full" key={file.name}>
                            <div className="w-full p-3 my-2 border border-[#E1E7EC] rounded-lg">
                              <div className="flex items-center justify-between relative pb-1">
                                <div className="flex items-center gap-3 flex-auto">
                                  {fileProgress.get(index) === 100 && fileLoading ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                                  ) : (
                                    <RiFileTextLine className="w-5 h-5 text-gray-400" />
                                  )}
                                  <div className="w-full text-start">
                                    <p className="text-sm font-medium">{file.name}</p>
                                    <p className="text-xs text-gray-500">
                                      {Math.round(file?.size / 1000) < 1024
                                        ? `${Math.round(file?.size / 1000)}KB`
                                        : `${(Math.round(file?.size / 1000) / 1024).toFixed(2)}MB`}
                                    </p>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => handleFileDelete(index)}
                                  className="text-gray-400 hover:text-red-500 transition-colors"
                                >
                                  <RiDeleteBinLine className="w-4 h-4" />
                                </button>
                              </div>

                              {/* Progress Bar */}
                              {(fileProgress.get(index) || 0) < 100 && (
                                <div className="py-[6px] flex items-center justify-center">
                                  <div className="flex-auto w-full">
                                    <div className="w-full bg-gray-200 rounded-full h-1">
                                      <div
                                        className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                                        style={{ width: `${fileProgress.get(index) || 0}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <DynamicsUrlModal
        show={showDynamicsModal}
        onClose={resetDynamicsModal}
        onConnect={handleDynamicsConnect}
      />
      <TwentyConnectModal
        show={showTwentyModal}
        onClose={resetTwentyModal}
        onConnect={handleTwentyConnect}
      />

      {/* Hidden navigation for parent component to handle */}
      <div className="hidden">
        <button type="button" onClick={handleSubmit} disabled={isLoading} />
        <button type="button" onClick={handleSkip} />
      </div>
    </>
  );
};

export default IntegrationsStep;
